import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";
import { isMobileBreakpoint } from "@theme/utilities";

const HISTORY_SETTLE_FALLBACK_MS = 750;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration | null} */
  #priorScrollRestoration = null;

  /**
   * The cart count to announce once the (rAF-deferred) drawer actually opens. Coalesces a rapid
   * burst of auto-open CartAddEvents to the LATEST count via the single durable #announcePendingCount
   * DialogOpenEvent handler — so assistive tech is never read a stale count twice, and nothing is
   * left orphaned if the component disconnects before the open lands.
   * @type {number | undefined}
   */
  #pendingAnnounceCount;

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) {
      return;
    }

    if (!history.state?.cartDrawerOpen) {
      // Prevent browser scroll restoration from fighting the dialog's explicit
      // scrollTo on close. The dialog locks body to position:fixed before
      // dispatching dialog:open, so window.scrollY is 0 at pushState time;
      // without this, history.back() animates the page to the top.
      this.#priorScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = "manual";
      history.pushState({ cartDrawerOpen: true }, "");
    }

    this.#historyAbortController = new AbortController();
    globalThis.addEventListener("popstate", this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #restoreScrollRestoration = () => {
    if (this.#priorScrollRestoration === null) {
      return;
    }
    history.scrollRestoration = this.#priorScrollRestoration;
    this.#priorScrollRestoration = null;
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      // Restore only after the back navigation settles — flipping back to
      // "auto" before the browser actually performs the (suppressed) restore
      // would re-enable the scroll-to-top we're preventing.
      globalThis.addEventListener("popstate", this.#restoreScrollRestoration, { once: true });
      history.back();
    } else {
      this.#restoreScrollRestoration();
    }
  };

  #handlePopState = async () => {
    if (!this.refs.dialog?.open) {
      return;
    }

    this.refs.dialog.style.setProperty("--dialog-drawer-closing-animation", "none");
    await this.closeDialog();
    this.refs.dialog.style.removeProperty("--dialog-drawer-closing-animation");
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {Event} event
   */
  #handleCartAdd = event => {
    const customEvent = /** @type {CustomEvent<{ resource?: { item_count?: number } }>} */ (event);
    const cartCount = customEvent.detail?.resource?.item_count;

    if (this.hasAttribute("auto-open") && !this.refs.dialog?.open) {
      // showDialog() DEFERS the real showModal() to a requestAnimationFrame, so the dialog is not
      // open yet and #announceCartCount's `dialog.open` guard would drop the count. Stash the count
      // for the durable #announcePendingCount DialogOpenEvent handler to announce once the drawer is
      // visible. A rapid second add before the open OVERWRITES this (coalesced — no double-announce),
      // and a disconnect mid-flight leaves no orphaned listener (the handler is removed in disconnect).
      this.#pendingAnnounceCount = cartCount;
      this.showDialog();
      return;
    }

    this.#announceCartCount(cartCount);
  };

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) {
      return;
    }

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector(".cart-drawer__content");
    const summary = dialog.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute("cart-summary-sticky", "false");
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute("cart-summary-sticky", ratio > this.#summaryThreshold ? "false" : "true");
  };

  /**
   * Announces the pending auto-open count (stashed by #handleCartAdd) once the deferred drawer
   * opens. Reads-and-clears the field so it fires at most once per burst and never on a manual open().
   */
  #announcePendingCount = () => {
    if (this.#pendingAnnounceCount === undefined) {
      return;
    }
    const count = this.#pendingAnnounceCount;
    this.#pendingAnnounceCount = undefined;
    this.#announceCartCount(count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) {
      return;
    }

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#announcePendingCount);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, "");
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#announcePendingCount);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#restoreScrollRestoration();
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements
      .whenDefined("shopify-payment-terms")
      .then(() => {
        const installmentsContent = document.querySelector("shopify-payment-terms")?.shadowRoot;
        const cta = installmentsContent?.querySelector("#shopify-installments-cta");
        cta?.addEventListener("click", this.closeDialog, { once: true });
      })
      .catch(() => {
        // Silently ignore if custom element is never defined
      });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Releases modal focus and scroll ownership before a same-document navigation.
   * On mobile, closing also removes the drawer's synthetic history entry; wait for
   * that popstate so the destination hash cannot be overwritten by history.back().
   * @returns {Promise<void>}
   */
  async closeForNavigation() {
    const historySettlement = this.#waitForNavigationHistory();
    await this.closeDialog();
    await historySettlement;
  }

  /** @returns {Promise<void>} */
  #waitForNavigationHistory() {
    if (!history.state?.cartDrawerOpen) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      const controller = new AbortController();
      let timeout = 0;
      const settle = () => {
        clearTimeout(timeout);
        controller.abort();
        // Chromium finishes a same-document traversal after dispatching popstate: in the same
        // task it re-scrolls to and re-focuses the restored entry's own fragment. Yield one
        // macrotask so the destination scroll and focus land after that, not before it.
        setTimeout(resolve, 0);
      };

      globalThis.addEventListener("popstate", settle, { once: true, signal: controller.signal });
      timeout = setTimeout(settle, HISTORY_SETTLE_FALLBACK_MS);
    });
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
